import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Zap, TrendingUp, ArrowRight, Coins } from 'lucide-react';
import { SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { MixedText } from '../../utils/rtl';
import UpgradeModal from './UpgradeModal';

export interface CreditErrorInfo {
  operation: string;
  operation_display_name: string;
  credits_needed: number;
  credits_available: number;
  upgrade_suggestion: {
    suggested_tier: string;
    message: string;
    benefits: string;
  };
}

interface CreditErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorInfo: CreditErrorInfo | null;
}

const CreditErrorModal: React.FC<CreditErrorModalProps> = ({
  isOpen,
  onClose,
  errorInfo
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  if (!isOpen || !errorInfo) return null;

  const creditPercentage = Math.min((errorInfo.credits_available / errorInfo.credits_needed) * 100, 100);
  const isFreeTier = errorInfo.upgrade_suggestion.suggested_tier === 'premium';

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
    // Don't close the credit error modal yet - let it stay open behind the upgrade modal
  };

  const handleCloseUpgrade = () => {
    setShowUpgradeModal(false);
    onClose(); // Close the credit error modal when upgrade modal closes
  };

  // Get operation display text
  const getOperationText = (operation: string) => {
    const operationMap: { [key: string]: { en: string, he: string } } = {
      'study_session_creation': { en: 'Create Study Session', he: 'יצירת מפגש לימוד' },
      'audio_upload': { en: 'Upload Audio File', he: 'העלאת קובץ שמע' },
      'document_upload': { en: 'Upload Document', he: 'העלאת מסמך' },
      'material_regeneration': { en: 'Regenerate Study Materials', he: 'יצירה מחדש של חומרי לימוד' },
      'question_regeneration': { en: 'Regenerate Questions', he: 'יצירה מחדש של שאלות' },
      'exam_processing': { en: 'Process Exam', he: 'עיבוד מבחן' },
    };
    
    return operationMap[operation]?.[isRTL ? 'he' : 'en'] || errorInfo.operation_display_name;
  };

  return (
    <>
      <div className={`fixed inset-0 flex items-center justify-center z-[70] p-4 ${isRTL ? 'rtl' : 'ltr'}`} 
           style={{ background: 'rgba(26, 15, 43, 0.9)' }}
           dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Space Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-red-500/10 to-vibrant-orange/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-br from-vibrant-orange/10 to-red-500/10 rounded-full blur-2xl animate-pulse delay-700"></div>
          <div className="absolute bottom-1/4 left-1/3 w-20 h-20 bg-gradient-to-br from-red-400/5 to-vibrant-orange/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>
        
        <SpaceCard 
          constellation="cassiopeia" 
          className={`relative w-full max-w-md shadow-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-500/5 to-vibrant-orange/5 ${isRTL ? 'rtl' : ''}`}
        >
          {/* Header */}
          <div className="relative">
            {/* Close Button */}
            <button
              onClick={onClose}
              className={`absolute top-6 z-10 p-2 rounded-lg text-surface-secondary hover:text-red-500 hover:bg-red-500/10 transition-all duration-200 ${isRTL ? 'left-6' : 'right-6'}`}
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Header Content */}
            <div className={`text-center py-6 px-6 ${isRTL ? 'font-hebrew' : ''}`}>
              {/* Astronaut with low battery/empty fuel */}
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-vibrant-orange/20 rounded-full flex items-center justify-center animate-credit-pulse">
                    <Zap className="w-10 h-10 text-red-500 animate-energy-drain" />
                  </div>
                  {/* Energy rings */}
                  <div className="absolute inset-0 rounded-full border-2 border-red-500/30 animate-ping"></div>
                  <div className="absolute inset-2 rounded-full border border-vibrant-orange/40 animate-ping delay-300"></div>
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-surface-primary font-hebrew mb-2">
                <MixedText>{isRTL ? 'לא מספיק קרדיטים' : 'Not Enough Credits'}</MixedText>
              </h2>
              
              <p className="text-surface-secondary font-hebrew text-sm">
                <MixedText>
                  {isRTL 
                    ? `צריך ${errorInfo.credits_needed} קרדיטים עבור "${getOperationText(errorInfo.operation)}"` 
                    : `Need ${errorInfo.credits_needed} credits for "${getOperationText(errorInfo.operation)}"`
                  }
                </MixedText>
              </p>
            </div>
          </div>

          {/* Credits Display */}
          <div className={`px-6 pb-6 ${isRTL ? 'font-hebrew' : ''}`}>
            {/* Credit Status Card */}
            <div className="bg-surface-primary/5 rounded-xl p-4 mb-6 border border-surface-muted/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Coins className="w-5 h-5 text-vibrant-orange" />
                  <span className="font-medium text-surface-primary font-hebrew">
                    <MixedText>{isRTL ? 'מצב קרדיטים' : 'Credit Status'}</MixedText>
                  </span>
                </div>
                <div className="text-sm text-surface-secondary font-hebrew">
                  <MixedText>
                    {`${errorInfo.credits_available} / ${errorInfo.credits_needed}`}
                  </MixedText>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-surface-muted/20 rounded-full h-3 mb-3">
                <div 
                  className="bg-gradient-to-r from-red-500 to-vibrant-orange h-3 rounded-full transition-all duration-500 relative overflow-hidden"
                  style={{ width: `${creditPercentage}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                </div>
              </div>
              
              {/* Credit Details */}
              <div className="space-y-2 text-xs text-surface-secondary">
                <div className="flex justify-between font-hebrew">
                  <span><MixedText>{isRTL ? 'זמין כעת:' : 'Available:'}</MixedText></span>
                  <span className="font-mono">{errorInfo.credits_available}</span>
                </div>
                <div className="flex justify-between font-hebrew">
                  <span><MixedText>{isRTL ? 'נדרש:' : 'Required:'}</MixedText></span>
                  <span className="font-mono text-red-500">{errorInfo.credits_needed}</span>
                </div>
                <div className="flex justify-between font-hebrew border-t border-surface-muted/10 pt-2">
                  <span><MixedText>{isRTL ? 'חסר:' : 'Short by:'}</MixedText></span>
                  <span className="font-mono font-bold text-red-500">
                    {errorInfo.credits_needed - errorInfo.credits_available}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`space-y-3 ${isRTL ? 'font-hebrew' : ''}`}>
              {isFreeTier && (
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full font-hebrew group relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #F76700, #F76700DD)',
                    boxShadow: '0 8px 24px rgba(247, 103, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  }}
                  onClick={handleUpgrade}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300 transform -skew-x-12" />
                  
                  <div className="relative flex items-center justify-center gap-2">
                    {!isRTL && <TrendingUp className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />}
                    <span className="text-base">
                      <MixedText>
                        {isRTL ? 'שדרג לפרימיום - ₪39/חודש' : 'Upgrade to Premium - ₪39/month'}
                      </MixedText>
                    </span>
                    {isRTL && <TrendingUp className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />}
                    {!isRTL && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />}
                  </div>
                </Button>
              )}
              
              <Button
                variant="secondary"
                size="md"
                className="w-full font-hebrew border border-surface-muted/30 hover:border-surface-muted/50"
                onClick={onClose}
              >
                <MixedText>{isRTL ? 'חזור' : 'Go Back'}</MixedText>
              </Button>
            </div>
          </div>
        </SpaceCard>
      </div>

      {/* Upgrade Modal - higher z-index to appear on top */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[80]">
          <UpgradeModal 
            isOpen={showUpgradeModal}
            onClose={handleCloseUpgrade}
          />
        </div>
      )}
    </>
  );
};

export default CreditErrorModal;